import {gsap} from 'gsap'

export default class TimelineModal {
  constructor(button) {
    this.button = button
    this.modalId = this.button.getAttribute('data-modal')
    this.modal = document.getElementById(this.modalId)
    
    if (!this.modal) return
    
    this.overlay = this.modal.querySelector('.modal-overlay')
    this.content = this.modal.querySelector('.modal-content')
    this.closeBtn = this.modal.querySelector('.modal-close')
    this.header = document.querySelector('.header')
    this.accordionItems = this.modal.querySelectorAll('.accordion-item')
    
    this.openModal = this.openModal.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.trapFocus = this.trapFocus.bind(this)
    
    this.init()
  }

  init() {
    this.addListeners()
  }

  addListeners() {
    this.button.addEventListener('click', this.openModal)
    
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.closeModal)
    }
    
    if (this.overlay) {
      this.overlay.addEventListener('click', this.closeModal)
    }
  }

  openModal(e) {
    e.preventDefault()
    
    // Hides the menu if not at top of page and not already hidden
    if (this.header) {
      const isAtTop = this.header.classList.contains('top')
      const isHidden = this.header.style.transform === 'translateY(-100%)'
      
      if (!isAtTop && !isHidden) {
        this.header.style.transform = 'translateY(-100%)'
      }
    }
    
    // Locks body scroll
    document.querySelector('html').classList.add('h-noscroll')
    
    // Removes display none and prepares for animation
    this.modal.style.display = 'block'
    
    // Sets initial state for animation
    gsap.set(this.overlay, { opacity: 0 })
    gsap.set(this.content, { x: -100, opacity: 0 })
    
    // Animates entrance
    const tl = gsap.timeline()
    
    tl.to(this.overlay, {
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out'
    })
    .to(this.content, {
      x: 0,
      opacity: 1,
      duration: 0.5,
      ease: 'power3.out'
    }, '-=0.1')
    
    // Handles focus trap
    setTimeout(() => {
      document.addEventListener('keydown', this.trapFocus)
      
      // Focus on first focusable element
      const focusableElements = this.modal.querySelectorAll(
        'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
      )
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
      }
    }, 600)
    
    // Handles ESC key close
    this.escapeHandler = (e) => {
      if (e.key === 'Escape') {
        this.closeModal(e)
      }
    }
    document.addEventListener('keydown', this.escapeHandler)
    
    // Opens first accordion by default
    if (this.accordionItems.length > 0) {
      const firstAccordion = this.accordionItems[0]
      firstAccordion.classList.add('open')
      const firstHiddenSection = firstAccordion.querySelector('.accordion-hidden-section')
      if (firstHiddenSection) {
        firstHiddenSection.style.display = 'block'
      }
    }
  }

  closeModal(e) {
    e.preventDefault()
    
    // Removes listeners
    document.removeEventListener('keydown', this.trapFocus)
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler)
    }
    
    // Animates exit
    const tl = gsap.timeline({
      onComplete: () => {
        this.modal.style.display = 'none'
        // Re-enables scroll
        document.querySelector('html').classList.remove('h-noscroll')
        // Resets accordion items
        this.accordionItems.forEach(item => {
          item.classList.remove('open')
          const hiddenSection = item.querySelector('.accordion-hidden-section')
          if (hiddenSection) {
            hiddenSection.style.display = 'none'
          }
        })
      }
    })
    
    tl.to(this.content, {
      x: -100,
      opacity: 0,
      duration: 0.4,
      ease: 'power3.in'
    })
    .to(this.overlay, {
      opacity: 0,
      duration: 0.3,
      ease: 'power2.in'
    }, '-=0.2')
  }

  trapFocus(e) {
    if (e.key !== 'Tab') return
    
    const focusableElements = this.modal.querySelectorAll(
      'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
    )
    
    if (focusableElements.length === 0) return
    
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]
    
    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault()
        lastElement.focus()
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }
  }

  static destroy() {
    if (TimelineModal.items) {
      TimelineModal.items.forEach(item => {
        if (item.modal && item.modal.style.display !== 'none') {
          document.querySelector('html').classList.remove('h-noscroll')
        }
      })
      TimelineModal.items = []
    }
  }

  static init() {
    console.debug('== TimelineModal components ==')
    
    const modalButtons = document.querySelectorAll('.modal-btn')
    
    if (modalButtons.length > 0) {
      TimelineModal.items = Array.from(modalButtons).map(
        (button, index) => new TimelineModal(button, index)
      )
    }
  }
}
